import * as Sentry from '@sentry/react'
import React from 'react'
import { Toaster } from '@/components/ui/toaster'
import { Toaster as Sonner } from '@/components/ui/sonner'
import { TooltipProvider } from '@/components/ui/tooltip'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { InterviewProvider } from './contexts/InterviewContext'
import { EmployeeProvider } from './contexts/EmployeeContext'
import ProtectedRoute from './components/ProtectedRoute'
import Index from './pages/Index'
import NotFound from './pages/NotFound'
import Interview from './pages/Interview'
import VerbalInterview from './pages/VerbalInterview'
import VideoInterview from './pages/VideoInterview'
import AdminQuestions from './pages/AdminQuestions'
import AdminQuestionCreation from './pages/AdminQuestionCreation'
import AdminResults from './pages/AdminResults'
import AdminAnalytics from './pages/AdminAnalytics'
import AdminEmployees from './pages/AdminEmployees'
import AdminEmployeeDetail from './pages/AdminEmployeeDetail'
import AdminTests from './pages/AdminTests'
import AdminTestDetail from './pages/AdminTestDetail'
import AdminLogin from './pages/AdminLogin'
import AdminPasswordUpdate from './pages/AdminPasswordUpdate'
import EmployeeLogin from './pages/EmployeeLogin'
import EmployeeDashboard from './pages/EmployeeDashboard'
import TestAccess from './pages/TestAccess'
import { Navigate } from 'react-router-dom'
import AdminResultDetail from './pages/AdminResultDetail'
import AdminTestCandidates from './pages/AdminTestCandidates'

const queryClient = new QueryClient()

const AppContent = () => {
  return (
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <InterviewProvider>
        <EmployeeProvider>
          <BrowserRouter>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Index />} />
              <Route path="/interview" element={<Interview />} />
              <Route path="/verbal-interview" element={<VerbalInterview />} />
              <Route path="/video-interview" element={<VideoInterview />} />
              <Route path="/employee/login" element={<EmployeeLogin />} />
              <Route path="/employee/dashboard" element={<EmployeeDashboard />} />
              <Route path="/test-access" element={<TestAccess />} />

              {/* Admin login route - accessible when not authenticated */}
              <Route
                path="/admin/login"
                element={
                  <ProtectedRoute>
                    <AdminLogin />
                  </ProtectedRoute>
                }
              />

              {/* Protected admin routes - require authentication */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requireAuth={true} adminOnly={true}>
                    <Navigate to="/admin/questions" replace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/questions"
                element={
                  <ProtectedRoute requireAuth={true} adminOnly={true}>
                    <AdminQuestions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/questions/create"
                element={
                  <ProtectedRoute requireAuth={true} adminOnly={true}>
                    <AdminQuestionCreation />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/questions/edit/:id"
                element={
                  <ProtectedRoute requireAuth={true} adminOnly={true}>
                    <AdminQuestionCreation />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/results"
                element={
                  <ProtectedRoute requireAuth={true} adminOnly={true}>
                    <AdminResults />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/results/:testId"
                element={
                  <ProtectedRoute requireAuth={true} adminOnly={true}>
                    <AdminTestCandidates />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/results/:testId/:assignmentId"
                element={
                  <ProtectedRoute requireAuth={true} adminOnly={true}>
                    <AdminResultDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/analytics"
                element={
                  <ProtectedRoute requireAuth={true} adminOnly={true}>
                    <AdminAnalytics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/employees"
                element={
                  <ProtectedRoute requireAuth={true} adminOnly={true}>
                    <AdminEmployees />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/employees/:id"
                element={
                  <ProtectedRoute requireAuth={true} adminOnly={true}>
                    <AdminEmployeeDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/tests"
                element={
                  <ProtectedRoute requireAuth={true} adminOnly={true}>
                    <AdminTests />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/tests/:id"
                element={
                  <ProtectedRoute requireAuth={true} adminOnly={true}>
                    <AdminTestDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/password-update"
                element={
                  <ProtectedRoute requireAuth={true} adminOnly={true}>
                    <AdminPasswordUpdate />
                  </ProtectedRoute>
                }
              />

              {/* 404 route */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </EmployeeProvider>
      </InterviewProvider>
    </TooltipProvider>
  )
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  </QueryClientProvider>
)

export default App
